import { FooterMenu, Loading } from "components"
import { InputSection, Journal, PrintBuildItem } from "./components"
import { DataContext } from "./contexts"
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import moment from "moment"
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"
import type { DataType } from "./types"
import { useApi, useToggle } from "utils"
import { type FormType, useDetail, validationSchema, checkQtyStatus } from "./utils"
import { useEffect, useMemo } from "react"

export default function BuildItem(): JSX.Element {
  const { product_id } = useParams()

  if (!product_id) {
    return (
      <section className="container my-3">
        <Loading errorText="No data available" />
      </section>
    )
  }

  return <FormSection productId={product_id} />
}

function FormSection(params: { productId: string }): JSX.Element {
  const methods = useForm<FormType>({
    defaultValues: {
      employee_id: "",
      location_id: "",
      qty_build_unit: 0,
      trx_code: "",
      transaction_date: moment().format("YYYY-MM-DD"),
      component_at_cost: 0,
      coa_overhead_id: 0,
      total_cac: 0,
      total_amount: 0,
      overhead: 0,
    },
    resolver: yupResolver(validationSchema),
  })

  const location_id = useWatch({
    control: methods.control,
    name: "location_id",
  })

  const { data, isLoading, isRefetching } = useDetail(params.productId, location_id)

  useEffect(() => {
    if (!data) return
    methods.setValue("component_at_cost", data.product.component_at_cost)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const contextValue = useMemo((): DataType | null => {
    if (!data) return null

    return {
      ...data,
      product: {
        ...data.product,
        approve_by: "-",
        approve_status: "-",
      },
    }
  }, [data])

  if (isLoading || !contextValue) {
    return (
      <section className="container my-3 flex flex-col gap-3">
        <Loading loading={isLoading} errorText="No data available" />
      </section>
    )
  }

  const product = contextValue.product

  return (
    <section className="container my-3 flex flex-col gap-3">
      <FormProvider {...methods}>
        <DataContext.Provider value={contextValue}>
          <InputSection ingredientsLoading={isRefetching} />

          <Footer
            ingredient={contextValue.ingredient}
            product_id={product.product_id}
          />
        </DataContext.Provider>
      </FormProvider>
    </section>
  )
}

function Footer(params: {
  ingredient: DataType["ingredient"]
  product_id: number
}): JSX.Element {
  const api = useApi()
  const navigate = useNavigate()
  const { isActive, toggle } = useToggle(false)

  const { control, formState, handleSubmit } = useFormContext<FormType>()
  const [qty_build_unit, location_id] = useWatch({
    control,
    name: ["qty_build_unit", "location_id"],
  })

  const onSubmit = (value: FormType): Promise<void> => {
    const locationId = Number(value.location_id)

    return new Promise<void>((resolve) => {
      toast.promise(
        api.post(
          "/list_build_item/save",
          {
            ...value,
            product_id: params.product_id,
            ingredients: params.ingredient.map((item) => ({
              build_id: 0,
              product_ingredient_id: item.product_id,
              qty_ingredient_unit: Number(item.qty) * qty_build_unit,
              mr_unit_id: item.unit_convention_id,
              location_id: locationId,
              avg_price: Number(item.avg_price),
              amount: item.qty * qty_build_unit * item.avg_price,
            })),
          }
        ),
        {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.detail?.message ?? err.response.data.message,
        }
      )
        .then(() => navigate(-1))
        .catch(() => {})
        .finally(() => resolve())
    })
  }

  const isDisabled: number = params.ingredient.reduce((acc, item) => {
    const status = checkQtyStatus(
      item.qty,
      qty_build_unit,
      item.soh,
      Number(item.avg_price)
    )

    if (!status) {
      return acc + 1
    }

    return acc
  }, 0)

  return (
    <section>
      <FooterMenu
        customElement={{ journal: <Journal /> }}
        save={{
          actiontype: "save",
          disabled: Boolean(isDisabled) || !location_id,
          loading: formState.isSubmitting ? "true" : undefined,
          onClick: handleSubmit(onSubmit),
        }}
        print={{ actiontype: "print", onClick: toggle }}
      />

      {isActive ? <PrintBuildItem toggle={toggle} /> : null}
    </section>
  )
}
